/**
 * use-kanban-tasks.ts
 *
 * Hook per il dettaglio task della Kanban Planner e le relative mutazioni:
 * creazione, modifica, completamento/riapertura e gestione allegati/copertina.
 * Complementare a `use-kanban-columns.ts` (board e colonne) e distinto da `use-tasks.ts`
 * (Kanban legacy a colonne-stato, non toccato).
 */

import { useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useCacheConfig } from '../contexts/cache-config-context';
import { tasksNew } from '../services/api';
import { kanbanColumnKeys } from './use-kanban-columns';
import { createImageThumbnail } from '../utils/image-thumbnail';
import type {
  ProjectTaskDetail,
  CreateKanbanTaskRequest,
  UpdateKanbanTaskRequest,
} from '../types/api';

// Chiave deliberatamente distinta da `useTask`/['tasks', id] di use-tasks.ts (Kanban legacy),
// per evitare collisioni di cache tra le due forme di task (summary legacy vs dettaglio Planner).
export const kanbanTaskKeys = {
  all: ['tasks', 'detail'] as const,
  detail: (id: number) => [...kanbanTaskKeys.all, id] as const,
  attachments: (id: number) => [...kanbanTaskKeys.all, id, 'attachments'] as const,
};

/**
 * Invalida tutte le query che dipendono dai dati di un task: le board Kanban, la lista task
 * legacy, il dettaglio del task stesso, i task/statistiche della richiesta collegata e le
 * statistiche della dashboard task.
 */
function invalidateTaskRelatedQueries(queryClient: QueryClient, task: ProjectTaskDetail) {
  queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.boards() });
  queryClient.invalidateQueries({ queryKey: ['tasks'] });
  queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.detail(task.id) });
  if (task.requestId) {
    const requestId = String(task.requestId);
    queryClient.invalidateQueries({ queryKey: ['request-tasks', requestId] });
    queryClient.invalidateQueries({ queryKey: ['request-tasks-stats', requestId] });
  }
  queryClient.invalidateQueries({ queryKey: ['task-dashboard-stats'] });
}

// ============== Query dettaglio ==============

export const useTaskDetail = (id: number) => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanTaskKeys.detail(id),
    queryFn: () => tasksNew.getTaskDetail(id),
    enabled: !!id,
    staleTime: getCacheTime('tasks'),
  });
};

// ============== Mutazioni task ==============

export const useCreateKanbanTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateKanbanTaskRequest) => tasksNew.createKanbanTask(dto),
    onSuccess: (task) => {
      invalidateTaskRelatedQueries(queryClient, task);
      notifications.show({
        title: 'Task creato',
        message: 'Il task è stato creato con successo',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile creare il task',
        color: 'red',
      });
    },
  });
};

export const useUpdateKanbanTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateKanbanTaskRequest }) =>
      tasksNew.updateKanbanTask(id, dto),
    onSuccess: (task) => {
      invalidateTaskRelatedQueries(queryClient, task);
      notifications.show({
        title: 'Task aggiornato',
        message: 'Le modifiche sono state salvate',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile aggiornare il task',
        color: 'red',
      });
    },
  });
};

export const useCompleteKanbanTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, completedAt }: { id: number; completedAt?: string }) =>
      tasksNew.completeKanbanTask(id, completedAt),
    onSuccess: (task) => {
      invalidateTaskRelatedQueries(queryClient, task);
      notifications.show({
        title: 'Task completato',
        message: 'Il task è stato segnato come completato',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile completare il task',
        color: 'red',
      });
    },
  });
};

export const useReopenKanbanTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tasksNew.reopenKanbanTask(id),
    onSuccess: (task) => {
      invalidateTaskRelatedQueries(queryClient, task);
      notifications.show({
        title: 'Task riaperto',
        message: 'Il task è stato riaperto',
        color: 'blue',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile riaprire il task',
        color: 'red',
      });
    },
  });
};

// ============== Allegati e copertina ==============

export const useTaskAttachments = (taskId: number) => {
  const { getCacheTime } = useCacheConfig();

  return useQuery({
    queryKey: kanbanTaskKeys.attachments(taskId),
    queryFn: () => tasksNew.getAttachments(taskId),
    enabled: !!taskId,
    // Più breve della validità assunta degli URL SAS (arrotondata all'ora successiva +1h lato
    // backend), così l'anteprima/download restano freschi senza rincorrere la scadenza.
    staleTime: getCacheTime('tasks'),
  });
};

export const useUploadTaskAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      taskId,
      file,
      setAsCover,
    }: {
      taskId: number;
      file: File;
      setAsCover?: boolean;
    }) => {
      const thumb =
        file.type === 'image/jpeg' || file.type === 'image/png' ? await createImageThumbnail(file) : null;
      return tasksNew.uploadAttachment(taskId, file, thumb, setAsCover);
    },
    onSuccess: (_attachment, variables) => {
      queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.detail(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.attachments(variables.taskId) });
      // La copertina può essere cambiata (prima immagine caricata o setAsCover): rinfresca le board.
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.boards() });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile caricare l\'allegato',
        color: 'red',
      });
    },
  });
};

export const useDeleteTaskAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, attachmentId }: { taskId: number; attachmentId: number }) =>
      tasksNew.deleteAttachment(taskId, attachmentId),
    onSuccess: (_void, variables) => {
      queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.detail(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.attachments(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.boards() });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile eliminare l\'allegato',
        color: 'red',
      });
    },
  });
};

export const useSetTaskCover = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, attachmentId }: { taskId: number; attachmentId: number | null }) =>
      tasksNew.setCover(taskId, attachmentId),
    onSuccess: (_cover, variables) => {
      queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.detail(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: kanbanTaskKeys.attachments(variables.taskId) });
      queryClient.invalidateQueries({ queryKey: kanbanColumnKeys.boards() });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Errore',
        message: error.message || 'Impossibile impostare la copertina',
        color: 'red',
      });
    },
  });
};
